import {
  CircleCheck,
  CircleDashed,
  CircleDot,
  CircleX,
  Clock,
  FileText,
} from 'lucide-react';
import type { ReactElement } from 'react';

import { SCHEDULE_PERIOD, WEEKDAY_MAP } from '@/types/commonConst';
import { ConstMap } from '@/utils/ConstMap';

export { SCRAPER_NAME } from '@/entities/(integration)/integrations';

/**
 * ScrapingJobType
 */
export enum SCRAPING_JOB_TYPE {
  ONETIME = 'ONETIME',
  SCHEDULED = 'SCHEDULED',
}

export const getScrapingJobTypeIcon = (type: SCRAPING_JOB_TYPE) => {
  switch (type) {
    case SCRAPING_JOB_TYPE.ONETIME:
      return FileText;
    case SCRAPING_JOB_TYPE.SCHEDULED:
      return Clock;
  }
};

export enum SCRAPING_SORT {
  RECENT = 'RECENT',
  ACCURACY = 'ACCURACY',
}

/**
 * ScrapingJobStatus
 */
export enum SCRAPING_JOB_STATUS {
  BEFORE_CONFIRM = 'BEFORE_CONFIRM',
  BEFORE_SCRAPING = 'BEFORE_SCRAPING',
  BLOCK_ANALYZING = 'BLOCK_ANALYZING',
  CANCELED = 'CANCELED',
  COMPLETED = 'COMPLETED',
  CREATE_FEEDBACK = 'CREATE_FEEDBACK',
  FAILED_CREATE_FEEDBACK = 'FAILED_CREATE_FEEDBACK',
  FAILED_SCRAPING = 'FAILED_SCRAPING',
  REJECTED = 'REJECTED',
  RUNNING_SCRAPING = 'RUNNING_SCRAPING',
  VALIDATE_SCRAPING = 'VALIDATE_SCRAPING',
  VIDEO_ANALYZING = 'VIDEO_ANALYZING',
}

export enum SCRAPER_CATEGORY {
  COMMERCE = 'COMMERCE',
  SOCIAL = 'SOCIAL',
  COMMUNITY = 'COMMUNITY',
}

export const getSchedulePeriodText = (schedule?: Schedule) => {
  if (!schedule) return '';
  switch (schedule?.period) {
    case SCHEDULE_PERIOD.DAILY:
      return 'Daily';
    case SCHEDULE_PERIOD.WEEKLY:
      if (typeof schedule.weekday !== 'number') {
        console.error(
          `[getSchedulePeriodText] Invalid schedule weekday: ${schedule.weekday}`,
        );
        return 'Error';
      }
      return WEEKDAY_MAP.get(schedule.weekday);
    case SCHEDULE_PERIOD.MONTHLY:
      return 'Monthly';
    default:
      console.error(
        `[getSchedulePeriodText] Invalid schedule period: ${schedule?.period}`,
      );
      return '';
  }
};

/**
 * Display-level grouping for scraping job statuses
 * - Keeps row-level status rendering unchanged via ScrapingStatusMap
 * - Aggregates multiple failure statuses into a single "Failed" bucket for summaries
 */
export enum SCRAPING_JOB_DISPLAY_STATUS {
  ALL = 'ALL',
  NEED_TO_APPROVAL = 'NEED_TO_APPROVAL',
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  REVIEWING = 'REVIEWING',
  VIDEO_ANALYZING = 'VIDEO_ANALYZING',
  FINALIZING = 'FINALIZING',
  DONE = 'DONE',
  CANCELED = 'CANCELED',
  REJECTED = 'REJECTED',
  FAILED = 'FAILED',
}

export enum SCRAPING_SCHEDULE_DISPLAY_STATUS {
  ALL = 'ALL',
  NEED_TO_APPROVAL = 'NEED_TO_APPROVAL',
  REJECTED = 'REJECTED',
  ACTIVE = 'ACTIVE',
}

export const ScrapingScheduleDisplayStatusToStatus = new Map<
  SCRAPING_SCHEDULE_DISPLAY_STATUS,
  SCRAPING_JOB_STATUS[]
>([
  [
    SCRAPING_SCHEDULE_DISPLAY_STATUS.NEED_TO_APPROVAL,
    [SCRAPING_JOB_STATUS.BEFORE_CONFIRM],
  ],
  [
    SCRAPING_SCHEDULE_DISPLAY_STATUS.ACTIVE,
    [SCRAPING_JOB_STATUS.BEFORE_SCRAPING],
  ],
]);

export const ScrapingScheduleDisplayStatusMap = new Map<
  SCRAPING_JOB_STATUS,
  SCRAPING_SCHEDULE_DISPLAY_STATUS
>([
  [
    SCRAPING_JOB_STATUS.BEFORE_CONFIRM,
    SCRAPING_SCHEDULE_DISPLAY_STATUS.NEED_TO_APPROVAL,
  ],
  [
    SCRAPING_JOB_STATUS.BEFORE_SCRAPING,
    SCRAPING_SCHEDULE_DISPLAY_STATUS.ACTIVE,
  ],
  [
    SCRAPING_JOB_STATUS.FAILED_SCRAPING,
    SCRAPING_SCHEDULE_DISPLAY_STATUS.ACTIVE,
  ],
  [SCRAPING_JOB_STATUS.REJECTED, SCRAPING_SCHEDULE_DISPLAY_STATUS.REJECTED],
]);

export const FAILED_STATUSES: SCRAPING_JOB_STATUS[] = [
  SCRAPING_JOB_STATUS.FAILED_CREATE_FEEDBACK,
  SCRAPING_JOB_STATUS.FAILED_SCRAPING,
];

export const ScrapingJobStatusToDisplayStatus = new ConstMap<
  SCRAPING_JOB_STATUS,
  SCRAPING_JOB_DISPLAY_STATUS
>([
  [
    SCRAPING_JOB_STATUS.BEFORE_CONFIRM,
    SCRAPING_JOB_DISPLAY_STATUS.NEED_TO_APPROVAL,
  ],
  [SCRAPING_JOB_STATUS.BEFORE_SCRAPING, SCRAPING_JOB_DISPLAY_STATUS.PENDING],
  [SCRAPING_JOB_STATUS.CANCELED, SCRAPING_JOB_DISPLAY_STATUS.CANCELED],
  [SCRAPING_JOB_STATUS.COMPLETED, SCRAPING_JOB_DISPLAY_STATUS.DONE],
  [SCRAPING_JOB_STATUS.CREATE_FEEDBACK, SCRAPING_JOB_DISPLAY_STATUS.FINALIZING],
  [SCRAPING_JOB_STATUS.BLOCK_ANALYZING, SCRAPING_JOB_DISPLAY_STATUS.FINALIZING],
  [
    SCRAPING_JOB_STATUS.FAILED_CREATE_FEEDBACK,
    SCRAPING_JOB_DISPLAY_STATUS.FAILED,
  ],
  [SCRAPING_JOB_STATUS.FAILED_SCRAPING, SCRAPING_JOB_DISPLAY_STATUS.FAILED],
  [SCRAPING_JOB_STATUS.REJECTED, SCRAPING_JOB_DISPLAY_STATUS.REJECTED],
  [
    SCRAPING_JOB_STATUS.RUNNING_SCRAPING,
    SCRAPING_JOB_DISPLAY_STATUS.IN_PROGRESS,
  ],
  [
    SCRAPING_JOB_STATUS.VALIDATE_SCRAPING,
    SCRAPING_JOB_DISPLAY_STATUS.REVIEWING,
  ],
  [
    SCRAPING_JOB_STATUS.VIDEO_ANALYZING,
    SCRAPING_JOB_DISPLAY_STATUS.VIDEO_ANALYZING,
  ],
]);

/**
 * Reverse lookup: DisplayStatus → JobStatus[]
 * Automatically derived from ScrapingJobStatusToDisplayStatus to avoid manual sync
 */
const reverseDisplayStatusMap = <K, V>(map: Map<K, V>): Map<V, K[]> => {
  const reversed = new Map<V, K[]>();
  for (const [key, value] of map.entries()) {
    const existing = reversed.get(value) || [];
    reversed.set(value, [...existing, key]);
  }
  return reversed;
};

const JobStatusByDisplayStatus = reverseDisplayStatusMap(
  ScrapingJobStatusToDisplayStatus,
);
const ScheduleStatusByDisplayStatus = reverseDisplayStatusMap(
  ScrapingScheduleDisplayStatusMap,
);

export const getJobStatusByDisplayStatus = (
  displayStatus: SCRAPING_JOB_DISPLAY_STATUS | SCRAPING_SCHEDULE_DISPLAY_STATUS,
  type: SCRAPING_JOB_TYPE,
): SCRAPING_JOB_STATUS[] => {
  if (type === SCRAPING_JOB_TYPE.ONETIME) {
    if (displayStatus === SCRAPING_JOB_DISPLAY_STATUS.ALL) {
      return [...ScrapingJobStatusToDisplayStatus.keys()];
    }
    return (
      JobStatusByDisplayStatus.get(
        displayStatus as SCRAPING_JOB_DISPLAY_STATUS,
      ) ?? []
    );
  }

  if (displayStatus === SCRAPING_SCHEDULE_DISPLAY_STATUS.ALL) {
    return [...ScrapingScheduleDisplayStatusMap.keys()];
  }
  return (
    ScheduleStatusByDisplayStatus.get(
      displayStatus as SCRAPING_SCHEDULE_DISPLAY_STATUS,
    ) ?? []
  );
};

export const ScrapingDisplayStatusMap = new ConstMap<
  SCRAPING_JOB_DISPLAY_STATUS | SCRAPING_SCHEDULE_DISPLAY_STATUS,
  { icon: ReactElement; text: string; tooltip?: string }
>([
  [
    SCRAPING_SCHEDULE_DISPLAY_STATUS.ALL,
    {
      icon: <></>,
      text: 'All',
      tooltip: 'All scraping schedules',
    },
  ],
  [
    SCRAPING_SCHEDULE_DISPLAY_STATUS.NEED_TO_APPROVAL,
    {
      icon: <CircleDashed size={14} />,
      text: 'Need to Approval',
      tooltip: 'Waiting for approval',
    },
  ],
  [
    SCRAPING_SCHEDULE_DISPLAY_STATUS.ACTIVE,
    {
      icon: <CircleCheck size={14} className="text-green-500" />,
      text: 'Active',
      tooltip: 'Active scraping schedules',
    },
  ],
  [
    SCRAPING_JOB_DISPLAY_STATUS.ALL,
    {
      icon: <></>,
      text: 'All',
      tooltip: 'All scraping jobs',
    },
  ],
  [
    SCRAPING_JOB_DISPLAY_STATUS.NEED_TO_APPROVAL,
    {
      icon: <CircleDashed size={14} />,
      text: 'Need to Approval',
      tooltip: 'Waiting for approval',
    },
  ],
  [
    SCRAPING_JOB_DISPLAY_STATUS.PENDING,
    {
      icon: <CircleDashed size={14} />,
      text: 'Pending',
      tooltip: 'Waiting to start scraping process',
    },
  ],
  [
    SCRAPING_JOB_DISPLAY_STATUS.IN_PROGRESS,
    {
      icon: <CircleDot size={14} className="text-yellow-500" />,
      text: 'In Progress',
      tooltip: 'Scraping data from the target platform',
    },
  ],
  [
    SCRAPING_JOB_DISPLAY_STATUS.REVIEWING,
    {
      icon: <CircleDot size={14} className="text-yellow-500" />,
      text: 'Reviewing',
      tooltip: 'Quality check in progress - validating scraped content',
    },
  ],
  [
    SCRAPING_JOB_DISPLAY_STATUS.VIDEO_ANALYZING,
    {
      icon: <CircleDot size={14} className="text-yellow-500" />,
      text: 'Video Analyzing',
      tooltip: 'Analyzing video content',
    },
  ],
  [
    SCRAPING_JOB_DISPLAY_STATUS.FINALIZING,
    {
      icon: <CircleDot size={14} className="text-green-500" />,
      text: 'Finalizing',
      tooltip: 'Creating feedback from processed data',
    },
  ],
  [
    SCRAPING_JOB_DISPLAY_STATUS.DONE,
    {
      icon: <CircleCheck size={14} className="text-green-500" />,
      text: 'Done',
      tooltip: 'Task completed successfully',
    },
  ],
  [
    SCRAPING_JOB_DISPLAY_STATUS.CANCELED,
    {
      icon: <CircleX size={14} className="text-destructive" />,
      text: 'Canceled',
      tooltip: 'Task was canceled before completion',
    },
  ],
  [
    SCRAPING_JOB_DISPLAY_STATUS.REJECTED,
    {
      icon: <CircleX size={14} className="text-destructive" />,
      text: 'Rejected',
      tooltip: 'Scraping request was rejected',
    },
  ],
  [
    SCRAPING_JOB_DISPLAY_STATUS.FAILED,
    {
      icon: <CircleX size={14} className="text-destructive" />,
      text: 'Failed',
      tooltip: 'Task failed during processing.',
    },
  ],
]);

export type ScrapingJobDisplayStatsResponse = Record<string, number>;

/**
 * Groups raw per-status counts into display-level counts
 */
export const groupScrapingStatsByDisplay = (
  type: SCRAPING_JOB_TYPE,
  stats?: Partial<Record<SCRAPING_JOB_STATUS, number>>,
): ScrapingJobDisplayStatsResponse => {
  const result: Record<string, number> = {};

  if (!stats) return result;

  const allowFields =
    type === SCRAPING_JOB_TYPE.ONETIME
      ? Object.values(SCRAPING_JOB_DISPLAY_STATUS).filter(
          (v) => v !== SCRAPING_JOB_DISPLAY_STATUS.ALL,
        )
      : [
          SCRAPING_SCHEDULE_DISPLAY_STATUS.NEED_TO_APPROVAL,
          SCRAPING_SCHEDULE_DISPLAY_STATUS.ACTIVE,
          SCRAPING_SCHEDULE_DISPLAY_STATUS.REJECTED,
        ];

  for (const field of allowFields) {
    const statuses = getJobStatusByDisplayStatus(field, type);
    if (!statuses) continue;
    for (const status of statuses) {
      result[
        type === SCRAPING_JOB_TYPE.ONETIME
          ? SCRAPING_JOB_DISPLAY_STATUS.ALL
          : SCRAPING_SCHEDULE_DISPLAY_STATUS.ALL
      ] =
        (result[
          type === SCRAPING_JOB_TYPE.ONETIME
            ? SCRAPING_JOB_DISPLAY_STATUS.ALL
            : SCRAPING_SCHEDULE_DISPLAY_STATUS.ALL
        ] || 0) + (stats[status] || 0);
      result[field] = (result[field] || 0) + (stats[status] || 0);
    }
  }
  return result;
};

export const AdminApprovalMap = new Map([
  [true, 'Wait for approval'],
  [false, 'Request without approval'],
]);

export const getDisplayStatusByStatus = (
  status: SCRAPING_JOB_STATUS,
  type: SCRAPING_JOB_TYPE,
): SCRAPING_JOB_DISPLAY_STATUS | SCRAPING_SCHEDULE_DISPLAY_STATUS => {
  let displayStatus:
    | SCRAPING_JOB_DISPLAY_STATUS
    | SCRAPING_SCHEDULE_DISPLAY_STATUS
    | undefined;
  if (type === SCRAPING_JOB_TYPE.ONETIME) {
    displayStatus = ScrapingJobStatusToDisplayStatus.get(status);
  } else {
    displayStatus = ScrapingScheduleDisplayStatusMap.get(status);
  }
  if (!displayStatus) {
    throw new Error(`Invalid job status: ${status} for type: ${type}`);
  }
  return displayStatus;
};
